import { DEFAULT_ROUTINE_ICON, type File, useStores } from '@littlebird/sdk';
import { adaptRoutineToViewModel } from '@littlebird/ui-utils';
import { useParams, useSearch } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';

import { Icons } from '@/assets/icons/icons';
import { HiddenFromUserIcon } from '@/components/blocks/HiddenFromUserIcon';
import { chatFindTunnel } from '@/components/blocks/Message/MessageList/useChatFind';
import { IconButton, Text, XStack } from '@/components/ui';
import { Header } from '@/components/ui/Header';
import { EntityIconChip, IconPicker } from '@/components/ui/icon-picker';
import { pageHeaderTunnel } from '@/design-system-v2/page-header-tunnel';
import { CanvasExportMenu } from '@/features/canvas/CanvasExportMenu';
import { ProjectBackLink } from '@/features/projects/projects';
import { useReportBody } from '@/features/routines/report-body';
import { formatNoteDate } from '@/features/routines/routines.utils';
import {
  routineReadState,
  useRoutineRefresh,
  useRoutineReportDetail,
} from '@/features/routines/use-routine-refresh';
import { settingsStore } from '@/stores/settings.store';
import { formatTimeFromHHmm } from '@/utils/date';

import { RoutineActionsMenu } from './components/routine-actions-menu';
import {
  RoutineReportCopyButton,
  RoutineReportSendToChat,
} from './components/routine-report-actions';
import { routinesStore } from './routines.state';

const formatRunDate = (date: string | Date) => {
  const formattedDate = formatNoteDate(date)
    .replace(/^(Today|Yesterday)/, (relativeDay) => relativeDay.toLowerCase())
    .replace(/, ([^,]+)$/, ' at $1');

  return `Ran ${formattedDate}`;
};

const RoutinesHeader = observer(() => {
  const {
    projects,
    reports: reportStore,
    routines: routineStore,
  } = useStores();
  useRoutineRefresh();
  const params = useParams({ strict: false });
  const { view } = useSearch({ strict: false }) as {
    view?: 'canvas' | 'chat' | 'configure';
  };
  const routineId = params.routineId ? Number(params.routineId) : null;
  const selectedReportId = routinesStore.useSelectedReportId(routineId);
  void settingsStore.timeFormat;
  const isReportsLoading = !routineReadState.hasLoadedReports;
  const selectedRoutine =
    routineId === null ? undefined : routineStore.getByReferenceId(routineId);
  const routineViewModel = useMemo(
    () =>
      selectedRoutine
        ? adaptRoutineToViewModel(selectedRoutine, [], {
            formatTime: formatTimeFromHHmm,
          })
        : null,
    [selectedRoutine]
  );
  const project = routineViewModel?.projectId
    ? (projects.getById(routineViewModel.projectId) ?? null)
    : null;
  const routineReports = reportStore.orderedReports.filter(
    (report) => report.reportTemplateId === routineId
  );
  const selectedRoutineReport = routineReports.find(
    (report) => report.referenceId === selectedReportId
  );
  const isReportDetailPending = useRoutineReportDetail(selectedRoutineReport);
  const headerReport = isReportDetailPending
    ? undefined
    : selectedRoutineReport;
  const selectedReportBody = useReportBody(
    headerReport?.chatId ? undefined : headerReport?.artifactFileIds
  );
  const selectedArtifactBody = useReportBody(headerReport?.artifactFileIds);
  const hasCanvas =
    selectedArtifactBody.isCanvas &&
    selectedArtifactBody.artifact.status === 'ready';
  const selectedCanvasFile =
    selectedReportBody.isCanvas &&
    selectedReportBody.artifact.status === 'ready'
      ? selectedReportBody.artifact.file
      : undefined;
  const isReportBodyPending =
    isReportsLoading ||
    isReportDetailPending ||
    (routineReports.length > 0 &&
      (!selectedRoutineReport ||
        !headerReport ||
        selectedReportBody.layout === 'pending'));
  const routineStatus = routineViewModel
    ? selectedRoutineReport && headerReport
      ? selectedReportBody.isCanvas || selectedReportBody.layout === 'pending'
        ? routineViewModel.schedule.summary
        : formatRunDate(selectedRoutineReport.scheduledAt)
      : routineViewModel.schedule.summary
    : '';

  return (
    <Header
      left={
        routineViewModel &&
        selectedRoutine &&
        !isReportBodyPending &&
        !selectedReportBody.isCanvas &&
        routineId !== null && (
          <XStack className="min-w-0 items-center gap-1">
            {project && (
              <>
                <ProjectBackLink project={project} />
                <Icons.chevronRight className="size-3 shrink-0 text-foreground-muted" />
              </>
            )}
            <IconPicker
              value={selectedRoutine.icon ?? DEFAULT_ROUTINE_ICON}
              onChange={(icon) =>
                void routineStore
                  .updateRoutine(selectedRoutine.referenceId, { icon })
                  .catch(() => undefined)
              }
            >
              <button
                type="button"
                aria-label={`Change icon for ${selectedRoutine.title}`}
                className="mr-1 inline-flex shrink-0 items-center justify-center rounded-md leading-none transition-opacity hover:opacity-80 focus-ring"
              >
                <EntityIconChip
                  icon={selectedRoutine.icon ?? DEFAULT_ROUTINE_ICON}
                  size={13}
                  className="rounded-md"
                />
              </button>
            </IconPicker>
            <Text className="max-w-72 truncate pr-1.5 text-sm">
              {routineViewModel.title}
            </Text>
            <HiddenFromUserIcon
              hidden={selectedRoutine.hiddenFromUser}
              className="mr-1.5"
            />
            <RoutineActionsMenu
              routine={selectedRoutine}
              statusLabel={routineStatus}
              hasCanvas={hasCanvas}
              view={view}
            />
          </XStack>
        )
      }
      right={
        selectedRoutine && routineId !== null ? (
          <Actions
            routineId={routineId}
            canvasFile={selectedCanvasFile}
            reportText={headerReport?.text.trim() ?? ''}
            isReportBodyPending={isReportBodyPending}
          />
        ) : (
          <pageHeaderTunnel.Out />
        )
      }
    />
  );
});

type ActionsProps = {
  routineId: number;
  canvasFile?: File;
  reportText: string;
  isReportBodyPending: boolean;
};

const Actions = observer(
  ({
    routineId,
    canvasFile,
    reportText,
    isReportBodyPending,
  }: ActionsProps) => {
    const showHistory = routinesStore.useShowHistory();

    return (
      <XStack className="items-center gap-1">
        <chatFindTunnel.Out />
        {!isReportBodyPending && canvasFile && (
          <>
            <CanvasExportMenu file={canvasFile} />
            <RoutineReportCopyButton text={reportText} />
            <RoutineReportSendToChat routineId={routineId} />
          </>
        )}
        {!showHistory && (
          <IconButton
            size="sm"
            icon={Icons.panelRight}
            scale={1.1}
            strokeWidth={1}
            tooltip="Toggle side panel"
            aria-label="Show runs panel"
            onClick={() => routinesStore.showRuns()}
          />
        )}
      </XStack>
    );
  }
);

export { RoutinesHeader };
